/* eslint-disable @typescript-eslint/no-explicit-any */

import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useLongPressAction } from "@/hooks/useLongPressAction";
import { Flag, Send } from "lucide-react";
import { XStack } from "tamagui";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import KindredSharedContent from "@/components/KindredSharedContent";
import MentionComposerField from "@/components/MentionComposerField";
import MessageActions from "@/components/MessageActions";
import ReportSheet from "@/components/ReportSheet";
import { MemberPageScaffold } from "@/components/MemberScaffold";
import { dispatchMentionNotifications, searchChannelMentionSuggestions } from "@/lib/mentions";
import { toast } from "sonner";
import type { MentionDraft } from "../../shared/community";
import {
  canEditOwnedContentOnly,
  canRemoveKinfeedOrChannelContent,
  isAdminModeratingOthersContent,
} from "../../shared/system";
import { AppButton, AppCard, AppCardBody, AppEmptyState, AppInput, AppLoadingState, AppText } from "../../ui-foundation";

const toTimestamp = (value?: string | null) => (value ? new Date(value).getTime() : 0);

const ReplyThread = () => {
  const { channelId, postId } = useParams<{ channelId: string; postId: string }>();
  const navigate = useNavigate();
  const { user, role } = useAuth();
  const longPressAction = useLongPressAction();
  const [post, setPost] = useState<any>(null);
  const [replies, setReplies] = useState<any[]>([]);
  const [replyReactions, setReplyReactions] = useState<any[]>([]);
  const [newReply, setNewReply] = useState("");
  const [newReplyMentions, setNewReplyMentions] = useState<MentionDraft[]>([]);
  const [sending, setSending] = useState(false);
  const [editingPost, setEditingPost] = useState(false);
  const [postEditText, setPostEditText] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState("");
  const [channel, setChannel] = useState<any>(null);
  const [isJoined, setIsJoined] = useState(false);
  const [membershipBusy, setMembershipBusy] = useState(false);
  const [replyActionsId, setReplyActionsId] = useState<string | null>(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportReplyId, setReportReplyId] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  const fetchData = async () => {
    if (!postId) return;
    const [{ data: p }, { data: rs }] = await Promise.all([
      supabase.from("posts").select("*, profiles(display_name, avatar_url)").eq("id", postId).single(),
      supabase.from("replies").select("*, profiles(display_name, avatar_url)").eq("post_id", postId).order("created_at", { ascending: true }),
    ]);
    const replyIds = (rs ?? []).map((reply: any) => reply.id);
    const { data: rxns } = replyIds.length
      ? await supabase.from("reply_reactions").select("*").in("reply_id", replyIds)
      : { data: [] as any[] };
    if (p) {
      setPost(p);
      const [{ data: currentChannel }, { data: membership }] = await Promise.all([
        supabase.from("channels").select("*").eq("id", p.channel_id).maybeSingle(),
        user
          ? supabase.from("channel_members").select("channel_id, joined_at, last_read_at").eq("channel_id", p.channel_id).eq("user_id", user.id).maybeSingle()
          : Promise.resolve({ data: null }),
      ]);
      setChannel(currentChannel);
      setIsJoined(Boolean(membership));
      if (membership && user) {
        const latestActivityAt = Math.max(
          toTimestamp(p.created_at),
          ...((rs ?? []) as any[]).map((reply: any) => toTimestamp(reply.created_at)),
          0,
        );
        const readCutoff = Math.max(
          toTimestamp(membership.last_read_at),
          toTimestamp(membership.joined_at),
        );

        if (latestActivityAt > readCutoff) {
          await supabase
            .from("channel_members")
            .update({ last_read_at: new Date().toISOString() })
            .eq("channel_id", p.channel_id)
            .eq("user_id", user.id);
        }
      }
      if (!editingPost) {
        setPostEditText((p as any).content ?? "");
      }
    }
    if (rs) setReplies(rs);
    setReplyReactions(rxns ?? []);
  };

  useEffect(() => { void fetchData(); }, [postId, user?.id]);

  useEffect(() => {
    if (!postId || !user?.id) return;
    const channel = supabase
      .channel(`replies-${postId}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "replies", filter: `post_id=eq.${postId}` }, () => fetchData())
      .on("postgres_changes", { event: "*", schema: "public", table: "reply_reactions" }, () => fetchData())
      .on("postgres_changes", { event: "*", schema: "public", table: "channel_members", filter: `user_id=eq.${user.id}` }, () => fetchData())
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [postId, user?.id]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [replies]);

  const sendReply = async () => {
    if (!newReply.trim() || !user || !postId || sending) return;
    setSending(true);
    const { error } = await supabase
      .from("replies")
      .insert({ post_id: postId, user_id: user.id, content: newReply.trim() });
    if (error) {
      toast.error(error.message);
      setSending(false);
      return;
    }
    await dispatchMentionNotifications({
      actorId: user.id,
      actorDisplayName:
        user.user_metadata?.display_name ?? user.email?.split("@")[0] ?? "Someone",
      content: newReply.trim(),
      mentions: newReplyMentions,
      bodyContext: `in ${channel?.name ?? "a channel"}`,
      entityType: "channel",
      entityId: channel?.id ?? channelId ?? "",
      url: channel?.id ? `/community/channel/${channel.id}` : undefined,
    });
    setNewReply("");
    setNewReplyMentions([]);
    setSending(false);
  };

  const deleteReply = async (replyId: string, removedByAdmin = false) => {
    await supabase.from("replies").delete().eq("id", replyId);
    fetchData();
    toast.success(removedByAdmin ? "Reply removed" : "Reply deleted");
  };

  const deletePost = async (removedByAdmin = false) => {
    await supabase.from("posts").delete().eq("id", postId);
    toast.success(removedByAdmin ? "Post removed" : "Post deleted");
    navigate(`/community/channel/${channelId}`);
  };

  const savePostEdit = async () => {
    if (!postEditText.trim()) return;
    await supabase
      .from("posts")
      .update({ content: postEditText.trim(), edited_at: new Date().toISOString() })
      .eq("id", postId);
    setEditingPost(false);
    fetchData();
    toast.success("Post updated");
  };

  const saveEdit = async (replyId: string) => {
    if (!editText.trim()) return;
    await supabase.from("replies").update({ content: editText.trim(), edited_at: new Date().toISOString() } as any).eq("id", replyId);
    setEditingId(null);
    fetchData();
  };

  const toggleReaction = async (replyId: string, emoji: string) => {
    if (!user) return;
    const existing = replyReactions.find((reaction) => reaction.reply_id === replyId && reaction.user_id === user.id && reaction.emoji === emoji);
    if (existing) {
      await supabase.from("reply_reactions").delete().eq("id", existing.id);
    } else {
      await supabase.from("reply_reactions").insert({ reply_id: replyId, user_id: user.id, emoji });
    }
    fetchData();
  };

  const getReactionsForReply = (replyId: string) => {
    const grouped: Record<string, { count: number; myReaction: boolean }> = {};
    replyReactions
      .filter((reaction) => reaction.reply_id === replyId)
      .forEach((reaction) => {
        if (!grouped[reaction.emoji]) {
          grouped[reaction.emoji] = { count: 0, myReaction: false };
        }
        grouped[reaction.emoji].count += 1;
        if (reaction.user_id === user?.id) {
          grouped[reaction.emoji].myReaction = true;
        }
      });
    return grouped;
  };

  const formatTime = (ts: string) => {
    const d = new Date(ts);
    const now = new Date();
    const diffH = (now.getTime() - d.getTime()) / 3600000;
    if (diffH < 1) return `${Math.max(1, Math.floor(diffH * 60))}m ago`;
    if (diffH < 24) return `${Math.floor(diffH)}h ago`;
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  };

  const handleJoin = async () => {
    if (!user || !channel?.id || membershipBusy) return;

    setMembershipBusy(true);
    const { error } = await supabase.from("channel_members").insert({
      channel_id: channel.id,
      user_id: user.id,
    });
    setMembershipBusy(false);

    if (error) {
      toast.error(error.message);
      return;
    }

    toast.success("Channel joined");
    await fetchData();
  };

  const handleLeave = async () => {
    if (!user || !channel?.id || membershipBusy) return;

    if (!window.confirm(`Leave ${channel?.name ?? "this channel"}? You can still read the thread later.`)) {
      return;
    }

    setMembershipBusy(true);
    const { error } = await supabase
      .from("channel_members")
      .delete()
      .eq("channel_id", channel.id)
      .eq("user_id", user.id);
    setMembershipBusy(false);

    if (error) {
      toast.error(error.message);
      return;
    }

    setNewReply("");
    toast.success("Channel left");
    await fetchData();
  };

  if (!post) {
    return (
      <MemberPageScaffold title="Thread" subtitle="Loading the original post and replies." fallbackPath="/communities?section=channels">
        <AppLoadingState title="Loading thread" body="Pulling in the post, replies, and channel state." />
      </MemberPageScaffold>
    );
  }

  const canEditPost = canEditOwnedContentOnly({ viewerId: user?.id, ownerId: post.user_id });
  const canRemovePost = canRemoveKinfeedOrChannelContent({ viewerId: user?.id, ownerId: post.user_id, role });
  const isAdminModeratingPost = isAdminModeratingOthersContent({ viewerId: user?.id, ownerId: post.user_id, role });

  return (
    <MemberPageScaffold
      title="Thread"
      subtitle={channel ? `${channel.emoji} ${channel.name}`.trim() : "Channel thread"}
      fallbackPath={channelId ? `/community/channel/${channelId}` : "/communities?section=channels"}
    >
        {/* Original post */}
        <div className="bg-muted/50 px-4 py-4">
          <div className="flex gap-2.5">
            <Avatar className="h-10 w-10 shrink-0">
              {post.profiles?.avatar_url && <AvatarImage src={post.profiles.avatar_url} />}
              <AvatarFallback className="bg-primary/20 text-primary text-sm font-semibold">
                {(post.profiles?.display_name ?? "K")[0]}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline gap-2">
                <span className="text-sm font-semibold text-foreground">{post.profiles?.display_name ?? "Member"}</span>
                <span className="text-xs text-muted-foreground">{formatTime(post.created_at)}</span>
                {canRemovePost ? (
                  <MessageActions
                    onEdit={canEditPost ? () => setEditingPost(true) : undefined}
                    onDelete={() => void deletePost(isAdminModeratingPost)}
                    deleteLabel={isAdminModeratingPost ? "Remove post" : "Delete post"}
                    showEdit={canEditPost}
                  />
                ) : null}
              </div>
              {editingPost ? (
                <div className="mt-1 space-y-2">
                  <AppInput value={postEditText} onChangeText={setPostEditText} />
                  <XStack gap="$sm">
                    <AppButton type="button" size="sm" onPress={() => void savePostEdit()}>Save</AppButton>
                    <AppButton
                      type="button"
                      tone="ghost"
                      size="sm"
                      onPress={() => {
                        setEditingPost(false);
                        setPostEditText(post.content);
                      }}
                    >
                      Cancel
                    </AppButton>
                  </XStack>
                </div>
              ) : (
                <>
                  <KindredSharedContent content={post.content} textClassName="mt-0.5 text-sm leading-relaxed text-foreground" />
                  {post.edited_at && <span className="text-[10px] text-muted-foreground">Edited</span>}
                </>
              )}
            </div>
          </div>
        </div>

        <div className="px-4 pt-4">
          <AppCard>
            <AppCardBody py="$sm">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1 space-y-1">
                  <p className="text-sm font-semibold text-foreground">
                    {isJoined ? "You are in this channel" : "Join this channel"}
                  </p>
                  <p className="text-sm leading-6 text-muted-foreground">
                    {isJoined
                      ? "Leave if you want this thread to become read-only for you again."
                      : "You can read first. Join when you want to reply or react."}
                  </p>
                </div>
                <AppButton
                  tone={isJoined ? "outline" : "primary"}
                  size="sm"
                  minWidth={132}
                  onPress={() => void (isJoined ? handleLeave() : handleJoin())}
                  disabled={membershipBusy}
                >
                  {membershipBusy ? "Saving..." : isJoined ? "Leave channel" : "Join channel"}
                </AppButton>
              </div>
            </AppCardBody>
          </AppCard>
        </div>

        {/* Replies */}
        <div className="space-y-4 px-4 py-4">
          {replies.length === 0 && (
            <AppEmptyState title="No replies yet" body="Join the conversation when you're ready." />
          )}
          {replies.map((r) => {
            const reactionSummary = getReactionsForReply(r.id);
            const selectedReaction = Object.entries(reactionSummary).find(([, value]) => value.myReaction)?.[0];

            return (
            <div
              key={r.id}
              className="space-y-1.5"
              {...longPressAction(() => setReplyActionsId(r.id))}
            >
              <div className="flex gap-2.5">
                <Avatar className="h-8 w-8 shrink-0">
                  {r.profiles?.avatar_url && <AvatarImage src={r.profiles.avatar_url} />}
                  <AvatarFallback className="bg-primary/20 text-primary text-xs font-semibold">
                    {(r.profiles?.display_name ?? "K")[0]}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  {(() => {
                    const canEditReply = canEditOwnedContentOnly({ viewerId: user?.id, ownerId: r.user_id });
                    const canRemoveReply = canRemoveKinfeedOrChannelContent({ viewerId: user?.id, ownerId: r.user_id, role });
                    const isAdminModeratingReply = isAdminModeratingOthersContent({ viewerId: user?.id, ownerId: r.user_id, role });

                    return (
                      <>
                  <div className="flex items-baseline gap-2">
                    <span className="text-sm font-semibold text-foreground">{r.profiles?.display_name ?? "Member"}</span>
                    <span className="text-xs text-muted-foreground">{formatTime(r.created_at)}</span>
                    <MessageActions
                      open={replyActionsId === r.id}
                      onOpenChange={(open) => setReplyActionsId(open ? r.id : null)}
                      selectedReaction={selectedReaction}
                      onReactionSelect={isJoined ? (emoji) => toggleReaction(r.id, emoji) : undefined}
                      actions={!canRemoveReply ? [
                        {
                          id: "report",
                          label: "Report",
                          icon: <Flag size={16} />,
                          onSelect: () => {
                            setReportReplyId(r.id);
                            setReportOpen(true);
                          },
                        },
                      ] : []}
                      onEdit={canEditReply ? () => { setEditingId(r.id); setEditText(r.content); } : undefined}
                      onDelete={canRemoveReply ? () => deleteReply(r.id, isAdminModeratingReply) : undefined}
                      deleteLabel={isAdminModeratingReply ? "Remove reply" : "Delete reply"}
                      showEdit={canEditReply}
                    />
                  </div>
                  {editingId === r.id ? (
                    <div className="mt-1 space-y-2">
                      <AppInput value={editText} onChangeText={setEditText} />
                      <XStack gap="$sm">
                        <AppButton type="button" size="sm" onPress={() => saveEdit(r.id)}>Save</AppButton>
                        <AppButton type="button" tone="ghost" size="sm" onPress={() => setEditingId(null)}>Cancel</AppButton>
                      </XStack>
                    </div>
                  ) : (
                    <>
                      <KindredSharedContent content={r.content} textClassName="mt-0.5 text-sm text-foreground" />
                      {r.edited_at && <span className="text-[10px] text-muted-foreground">Edited</span>}
                    </>
                  )}
                      </>
                    );
                  })()}
                </div>
              </div>
              <div className="ml-10 flex flex-wrap items-center gap-1.5">
                {Object.entries(reactionSummary).map(([emoji, { count, myReaction }]) => (
                  <button
                    key={emoji}
                    onClick={() => toggleReaction(r.id, emoji)}
                    disabled={!isJoined}
                    className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs transition-colors ${
                      myReaction ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"
                    } ${!isJoined ? "cursor-not-allowed opacity-70" : ""}`}
                  >
                    {emoji} {count}
                  </button>
                ))}
              </div>
            </div>
            );
          })}
          <div ref={bottomRef} />
        </div>

      <div className="shrink-0 border-t border-border bg-card px-4 py-3">
        {isJoined ? (
          <XStack alignItems="center" gap="$sm">
            <MentionComposerField
              value={newReply}
              onChangeText={setNewReply}
              mentions={newReplyMentions}
              onMentionsChange={setNewReplyMentions}
              lookupCandidates={(query) =>
                searchChannelMentionSuggestions(channel?.id ?? channelId ?? "", query, user?.id)
              }
              placeholder="Write a reply..."
              fieldProps={{ flex: 1 }}
              inputProps={{
                flex: 1,
                onKeyDown: (event: any) => {
                  if (event.key === "Enter") {
                    void sendReply();
                  }
                },
              }}
            />
            <AppButton
              type="button"
              width={44}
              minWidth={44}
              height={44}
              px={0}
              onPress={sendReply}
              disabled={sending || !newReply.trim()}
              aria-label="Send reply"
            >
              <Send size={16} />
            </AppButton>
          </XStack>
        ) : (
          <AppText fontSize="$sm" lineHeight="$sm" color="$colorMuted">
            Join this channel to reply in the thread and add reactions.
          </AppText>
        )}
      </div>
      <ReportSheet
        open={reportOpen}
        onOpenChange={setReportOpen}
        contentType="channel_reply"
        contentId={reportReplyId ?? undefined}
      />
    </MemberPageScaffold>
  );
};

export default ReplyThread;
